﻿// const units: number[] = [1, 1000, 60000, 3600000];


import IClonable from "../types/IClonable";
import IEquatable from "../types/IEquatable";
import {TimeSpan} from "./TimeSpan";

const TICKS_PER_DAY = 86400000;
const TICKS_PER_HOUR = 3600000;
const TICKS_PER_MINUTE = 60000;
const TICKS_PER_SECOND = 1000;

function pad(value: number): string {
	return ("0" + value).slice(-2);
}

function toISOString(time: TimeOnly): string {
	const ms = time.getMilliseconds();
	const short = `${pad(time.getHours())}:${pad(time.getMinutes())}:${pad(time.getSeconds())}`;
	if (ms === 0) {
		return short;
	}
	return `${short}.${("00" + ms).slice(-3)}`;
}

function timeToTicks(hours: number, minutes: number, seconds: number, milliseconds: number): number {
	if (isNaN(hours)) hours = 0;
	if (isNaN(minutes)) minutes = 0;
	if (isNaN(seconds)) seconds = 0;
	if (isNaN(milliseconds)) milliseconds = 0;

	if (hours < 0 || hours > 23) throw new Error("Invalid hours");
	if (minutes < 0 || minutes > 59) throw new Error("Invalid minutes");
	if (seconds < 0 || seconds > 59) throw new Error("Invalid seconds");
	if (milliseconds < 0 || milliseconds > 999) throw new Error("Invalid milliseconds");

	return hours * TICKS_PER_HOUR + minutes * TICKS_PER_MINUTE + seconds * TICKS_PER_SECOND + milliseconds;
}

function parseString(value: string): number {
	// Accept both HH:MM (the default format from <input type=time>) and HH:MM:SS[.fff].
	const [hours, minutes, maybeSeconds] = value.split(":");
	const [seconds, milliseconds] = (maybeSeconds ?? "0").split(".");
	return timeToTicks(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10), parseInt(milliseconds, 10));
}


export class TimeOnly implements IEquatable<TimeOnly>, IClonable<TimeOnly> {
	private static _zero: TimeOnly | null = null;
	private readonly _ticks: number;

	private constructor(ticks: number) {
		//.NET TimeOnly semantics: arithmetic wraps modulo 24 h (23:00 + 2 h = 01:00), never negative
		this._ticks = ((ticks % TICKS_PER_DAY) + TICKS_PER_DAY) % TICKS_PER_DAY;
	}

	get [Symbol.toStringTag](): string {
		return `TimeOnly ${toISOString(this)}`;
	}

	[Symbol.toPrimitive](hint: string): number | string {
		if (hint === "number") {
			return this._ticks;
		}

		return toISOString(this);
	}

	/**
	 * Adds the specified number of hours to the value of this instance.
	 * @param hours The number of hours to add. Can be negative to subtract hours.
	 * @returns A new TimeOnly that adds the specified number of hours to the value of this instance.
	 */
	addHours(hours: number): TimeOnly {
		return new TimeOnly(this._ticks + hours * TICKS_PER_HOUR);
	}

	/**
	 * Adds the specified number of minutes to the value of this instance.
	 * @param minutes The number of minutes to add. Can be negative to subtract minutes.
	 * @returns A new TimeOnly that adds the specified number of minutes to the value of this instance.
	 */
	addMinutes(minutes: number): TimeOnly {
		return new TimeOnly(this._ticks + minutes * TICKS_PER_MINUTE);
	}

	/**
	 * Adds the specified number of seconds to the value of this instance.
	 * @param seconds The number of seconds to add. Can be negative to subtract seconds.
	 * @returns A new TimeOnly that adds the specified number of seconds to the value of this instance.
	 */
	addSeconds(seconds: number): TimeOnly {
		return new TimeOnly(this._ticks + seconds * TICKS_PER_SECOND);
	}

	/**
	 * Creates a new object that is a copy of the current instance.
	 * @returns A new TimeOnly object that is a copy of this instance.
	 */
	clone(): TimeOnly {
		return new TimeOnly(this._ticks);
	}

	/**
	 * Compares the value of this instance to a specified TimeOnly object, null or undefined.
	 * @param other The object to compare to the current instance.
	 * @returns A signed number indicating the relative values:
	 * - Less than 0 if this instance is earlier than the other
	 * - 0 if this instance is equal to the other
	 * - Greater than 0 if this instance is later than the other
	 * - 1 if other is null or undefined
	 */
	compareTo(other: TimeOnly | null | undefined): number {
		if (other == null) {
			return 1;
		}

		return this._ticks - other._ticks;
	}

	/**
	 * Returns the TimeSpan value represented by the difference between the value of this instance and the specified TimeOnly object.
	 * @param value The TimeOnly value to subtract.
	 * @returns A TimeSpan value that represents the duration between the value of the current instance and the specified TimeOnly object.
	 */
	diff(value: TimeOnly): TimeSpan {
		return TimeSpan.fromTicks(this._ticks - value._ticks);
	}

	/**
	 * Determines whether the specified object is equal to the current instance.
	 * @param other The object to compare to this instance.
	 * @returns true if the specified object is equal to the current instance; otherwise, false.
	 */
	equals(other: TimeOnly | null | undefined): boolean {
		if (other == null) {
			return false;
		}

		return this._ticks === other._ticks;
	}

	/**
	 * Returns the hour component of the time represented by this instance.
	 * @returns The hour component, expressed as a value between 0 and 23.
	 */
	getHours(): number {
		return (this._ticks / TICKS_PER_HOUR | 0) % 24;
	}

	/**
	 * Returns the milliseconds component of the time represented by this instance.
	 * @returns The milliseconds component, expressed as a value between 0 and 999.
	 */
	getMilliseconds(): number {
		return this._ticks % TICKS_PER_SECOND;
	}

	/**
	 * Returns the minute component of the time represented by this instance.
	 * @returns The minute component, expressed as a value between 0 and 59.
	 */
	getMinutes(): number {
		return (this._ticks / TICKS_PER_MINUTE | 0) % 60;
	}

	/**
	 * Returns the seconds component of the time represented by this instance.
	 * @returns The seconds component, expressed as a value between 0 and 59.
	 */
	getSeconds(): number {
		return (this._ticks / TICKS_PER_SECOND | 0) % 60;
	}

	/**
	 * Returns the number of ticks that represent the time of this instance.
	 * @returns The number of ticks (milliseconds) that represent the time of this instance.
	 */
	getTicks(): number {
		return this._ticks;
	}

	/**
	 * Returns the total number of hours represented by this instance.
	 * @returns The total hours, including fractional hours.
	 */
	getTotalHours(): number {
		return this._ticks / TICKS_PER_HOUR;
	}

	/**
	 * Returns the total number of minutes represented by this instance.
	 * @returns The total minutes, including fractional minutes.
	 */
	getTotalMinutes(): number {
		return this._ticks / TICKS_PER_MINUTE;
	}

	/**
	 * Returns the total number of seconds represented by this instance.
	 * @returns The total seconds, including fractional seconds.
	 */
	getTotalSeconds(): number {
		return this._ticks / TICKS_PER_SECOND;
	}

	/**
	 * Returns a Date object representing this time on an arbitrary date (January 1, 1980).
	 * @returns A Date object with the time components of this instance.
	 */
	getUtcDate(): Date {
		return new Date(1980, 0, 1, this.getHours(), this.getMinutes(), this.getSeconds(), this.getMilliseconds());
	}

	static endOfDay(): TimeOnly {
		return TimeOnly.fromComponents(23, 59, 59, 999);
	}

	/**
	 * Determines whether this time falls between the specified start and end times.
	 * Handles cases where the time range crosses midnight.
	 * @param start The start time of the range.
	 * @param end The end time of the range.
	 * @returns true if this time is between start (inclusive) and end (exclusive); otherwise, false.
	 */
	isBetween(start: TimeOnly, end: TimeOnly): boolean {
		const startTicks = start._ticks;
		const endTicks = end._ticks;

		return startTicks <= endTicks
			? startTicks <= this._ticks && endTicks > this._ticks
			: startTicks <= this._ticks || endTicks > this._ticks;
	}

	/**
	 * Converts the value of the current TimeOnly object to its equivalent JSON string representation.
	 * @returns A string in ISO 8601 time format (HH:mm:ss or HH:mm:ss.mmm).
	 */
	toJSON(): string {
		return toISOString(this);
	}

	/**
	 * Converts the value of the current TimeOnly object to its equivalent string representation.
	 * @returns A string in ISO 8601 time format (HH:mm:ss or HH:mm:ss.mmm).
	 */
	toString(): string {
		return toISOString(this);
	}

	/**
	 * Creates a TimeOnly instance from time components.
	 * @param hours The hours (0-23).
	 * @param minutes The minutes (0-59). Defaults to 0.
	 * @param seconds The seconds (0-59). Defaults to 0.
	 * @param milliseconds The milliseconds (0-999). Defaults to 0.
	 * @returns A new TimeOnly instance.
	 * @throws Error if any component is outside its valid range.
	 */
	public static fromComponents(hours: number, minutes: number = 0, seconds: number = 0, milliseconds: number = 0): TimeOnly {
		return new TimeOnly(timeToTicks(hours, minutes, seconds, milliseconds));
	}

	/**
	 * Creates a TimeOnly instance from a time string in ISO 8601 format.
	 * @param value A time string in the format "HH:MM:SS" or "HH:MM:SS.mmm".
	 * @returns A new TimeOnly instance.
	 */
	public static fromIsoString(value: string): TimeOnly {
		return new TimeOnly(parseString(value));
	}

	/**
	 * Creates a TimeOnly instance from the specified number of ticks (milliseconds).
	 * @param ticks The number of ticks (milliseconds).
	 * @returns A new TimeOnly instance.
	 */
	public static fromTicks(ticks: number): TimeOnly {
		return new TimeOnly(ticks);
	}

	/**
	 * Creates a TimeOnly instance representing midnight (00:00:00).
	 * @returns A TimeOnly instance with all components set to zero.
	 */
	public static zero(): TimeOnly {
		return this._zero ??= new TimeOnly(0);
	}
}
